/**
 * Analysis Wizard Component
 * Multi-step flow for creating a new analysis
 * Step 1: Select Dataset
 * Step 2: Select Model
 * Step 3: Configure Analysis
 * Step 4: Review & Run
 */

import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { analysisAPI } from "../../utils/analysisAPI";
import { datasetsAPI } from "../../utils/datasetsAPI";
import { organizationsAPI } from "../../utils/organizationsAPI";
import { useAuth } from "../../context/AuthContext";
import { LoadingSpinner } from "../../components/Loaders";
import "./AnalysisWizard.css";

const MODEL_INFO = {
  REGRESSION: {
    label: "Regression",
    description: "Predict continuous numerical values",
    models: [
      {
        value: "linear_regression",
        label: "Linear Regression",
        desc: "Simple linear relationships",
      },
      {
        value: "random_forest_regression",
        label: "Random Forest",
        desc: "Ensemble method for robust predictions",
      },
      {
        value: "polynomial_regression",
        label: "Polynomial Regression",
        desc: "Non-linear patterns",
      },
      {
        value: "gradient_boosting_regression",
        label: "Gradient Boosting",
        desc: "Sequential ensemble for high accuracy",
      },
      {
        value: "huber_regression",
        label: "Huber Regression",
        desc: "Robust regression with outlier tolerance",
      },
    ],
  },
  CLASSIFICATION: {
    label: "Classification",
    description: "Predict categorical outcomes",
    models: [
      {
        value: "logistic_regression",
        label: "Logistic Regression",
        desc: "Binary/multi-class classification",
      },
      {
        value: "random_forest_classification",
        label: "Random Forest",
        desc: "High accuracy classification",
      },
      {
        value: "svm",
        label: "Support Vector Machine",
        desc: "High-dimensional classification",
      },
      {
        value: "gradient_boosting_classification",
        label: "Gradient Boosting Classifier",
        desc: "Sequential ensemble for high accuracy",
      },
    ],
  },
  CLUSTERING: {
    label: "Clustering",
    description: "Group similar data points",
    models: [
      { value: "kmeans", label: "K-Means", desc: "Partition into k clusters" },
      {
        value: "hierarchical",
        label: "Hierarchical Clustering",
        desc: "Build cluster hierarchy",
      },
      { value: "dbscan", label: "DBSCAN", desc: "Density-based clustering" },
    ],
  },
  TIME_SERIES: {
    label: "Time Series",
    description: "Forecast future values",
    models: [
      { value: "arima", label: "ARIMA", desc: "Auto-regressive patterns" },
      {
        value: "exponential_smoothing",
        label: "Exponential Smoothing",
        desc: "Weighted averages",
      },
      { value: "prophet", label: "Facebook Prophet", desc: "With seasonality" },
    ],
  },
  ANOMALY_DETECTION: {
    label: "Anomaly Detection",
    description: "Identify outliers and anomalies",
    models: [
      {
        value: "isolation_forest",
        label: "Isolation Forest",
        desc: "Isolation-based detection",
      },
      {
        value: "local_outlier_factor",
        label: "Local Outlier Factor",
        desc: "Density-based outliers",
      },
      {
        value: "one_class_svm",
        label: "One-Class SVM",
        desc: "Single-class support vector",
      },
    ],
  },
};

const AnalysisWizard = () => {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [step, setStep] = useState(1);
  const [datasets, setDatasets] = useState([]);
  const [selectedDataset, setSelectedDataset] = useState(null);
  const [datasetColumns, setDatasetColumns] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedModel, setSelectedModel] = useState("");
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [targetVariable, setTargetVariable] = useState("");
  const [otherParams, setOtherParams] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadOrganizations();
  }, []);

  useEffect(() => {
    if (selectedOrgId) {
      loadDatasets();
    }
  }, [selectedOrgId]);

  const loadOrganizations = async () => {
    try {
      const response = await organizationsAPI.getAccessible();
      const data = response?.data || response;
      const orgsList = Array.isArray(data) ? data : data?.results || [];
      setOrganizations(orgsList);

      // Set initial organization from URL param or query string
      const urlOrgId = orgId || searchParams.get("org");
      if (urlOrgId) {
        setSelectedOrgId(Number(urlOrgId));
      } else if (orgsList.length > 0) {
        setSelectedOrgId(orgsList[0].id);
      }
    } catch (err) {
      setError("Failed to load organizations");
    }
  };

  const loadDatasets = async () => {
    if (!selectedOrgId) {
      setError("No organization selected");
      return;
    }

    try {
      setLoading(true);
      const response = await datasetsAPI.list(selectedOrgId);
      const data = response?.data || response;
      const datasetsList = Array.isArray(data) ? data : data?.results || [];

      const formattedDatasets = datasetsList.map((ds) => ({
        id: ds.id,
        name: ds.name,
        label: ds.label,
        rows: ds.rows_count || 0,
        columns: (ds.columns || []).map((col) => ({
          key: col.key,
          name: col.label || col.key,
          type: col.column_type,
        })),
      }));

      setDatasets(formattedDatasets);
      setError(null);
    } catch (err) {
      setError("Failed to load datasets");
      setDatasets([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOrgChange = (orgId) => {
    setSelectedOrgId(Number(orgId));
    setSelectedDataset(null);
    setDatasets([]);
    setStep(1);
  };

  const handleDatasetSelect = (datasetId) => {
    const dataset = datasets.find((d) => d.id === datasetId);
    setSelectedDataset(dataset);
    setDatasetColumns(dataset.columns || []);
    setSelectedFeatures([]);
    setTargetVariable("");
  };

  const handleFeatureToggle = (columnKey) => {
    setSelectedFeatures((prev) =>
      prev.includes(columnKey)
        ? prev.filter((f) => f !== columnKey)
        : [...prev, columnKey],
    );
  };

  const handleSubmit = async () => {
    if (!selectedOrgId) {
      setError("No organization selected");
      return;
    }

    if (!selectedDataset?.id) {
      setError("No dataset selected");
      return;
    }

    if (
      ["REGRESSION", "CLASSIFICATION", "TIME_SERIES"].includes(
        selectedCategory,
      ) &&
      !targetVariable
    ) {
      setError(
        "Please select a target variable (the column to predict) in the Configure step.",
      );
      return;
    }

    if (selectedFeatures.length === 0) {
      setError("Please select at least one feature.");
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const analysisData = {
        client_id: selectedOrgId,
        data_table: String(selectedDataset.name || selectedDataset.id || ""),
        model_category: selectedCategory,
        model_name: selectedModel,
        configuration: {
          features: selectedFeatures,
          target_variable: targetVariable || null,
          ...otherParams,
        },
      };

      // Use the selected client organization for the API route
      // (analysts may not have a home org, but they ARE assigned to client orgs)
      const orgIdForRoute = selectedOrgId;
      if (!orgIdForRoute) {
        setError(
          "No organization selected. Please select a client organization.",
        );
        return;
      }
      const response = await analysisAPI.createAnalysis(
        orgIdForRoute,
        analysisData,
      );
      const created = response?.data ?? response;
      const analysisId = created?.id;
      if (!analysisId) {
        setError("Analysis was created but could not open it. Check the list.");
        return;
      }
      navigate(`/analyst/analysis/${analysisId}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const selectedOrg = organizations.find((o) => o.id === selectedOrgId);

  if (organizations.length === 0 && !loading) {
    return (
      <div className="analysis-wizard">
        <div className="wizard-header">
          <h1>New Analysis</h1>
        </div>
        <div className="alert alert-error">
          No client organizations assigned to you yet.
        </div>
      </div>
    );
  }

  return (
    <div className="analysis-wizard">
      <div className="wizard-header">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <h1>New Analysis</h1>
          {organizations.length > 0 && (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "4px" }}
            >
              <label style={{ fontSize: "12px", color: "var(--text-grey)" }}>
                Client Organization
              </label>
              <select
                value={selectedOrgId || ""}
                onChange={(e) => handleOrgChange(e.target.value)}
                style={{
                  padding: "8px 12px",
                  border: "1px solid #ddd",
                  borderRadius: "6px",
                  minWidth: "200px",
                }}
              >
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
        <div className="step-indicator">
          {[1, 2, 3, 4].map((s) => (
            <div
              key={s}
              className={`step ${s === step ? "active" : ""} ${s < step ? "completed" : ""}`}
            >
              <span className="step-number">{s}</span>
              <span className="step-label">
                {s === 1 && "Dataset"}
                {s === 2 && "Model"}
                {s === 3 && "Configure"}
                {s === 4 && "Review"}
              </span>
            </div>
          ))}
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="wizard-content">
        {/* Step 1: Select Dataset */}
        {step === 1 && (
          <div className="wizard-step">
            <h2>Select Dataset</h2>
            <p className="step-description">
              Choose a dataset from{" "}
              {selectedOrg?.name || "selected organization"} to analyze
            </p>

            {loading ? (
              <div style={{ textAlign: "center", padding: "40px" }}>
                <LoadingSpinner />
                <p style={{ marginTop: "16px", color: "var(--text-grey)" }}>
                  Loading datasets...
                </p>
              </div>
            ) : datasets.length === 0 ? (
              <div style={{ textAlign: "center", padding: "40px" }}>
                <p style={{ color: "var(--text-grey)" }}>
                  No datasets found for {selectedOrg?.name}
                </p>
              </div>
            ) : (
              <div className="dataset-selection">
                {datasets.map((dataset) => (
                  <div
                    key={dataset.id}
                    className={`dataset-card ${selectedDataset?.id === dataset.id ? "selected" : ""}`}
                    onClick={() => handleDatasetSelect(dataset.id)}
                  >
                    <h3>{dataset.name}</h3>
                    {dataset.label && (
                      <p className="dataset-label">{dataset.label}</p>
                    )}
                    <p className="dataset-info">{dataset.rows || 0} rows</p>
                    <div className="columns-preview">
                      <small>Columns: {dataset.columns?.length || 0}</small>
                      <div className="column-tags">
                        {dataset.columns?.slice(0, 3).map((col) => (
                          <span key={col.key} className="tag">
                            {col.name}
                          </span>
                        ))}
                        {(dataset.columns?.length || 0) > 3 && (
                          <span className="tag">
                            +{dataset.columns.length - 3}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step 2: Select Model */}
        {step === 2 && (
          <div className="wizard-step">
            <h2>Select Model</h2>
            <p className="step-description">
              Choose analysis type and specific model
            </p>

            <div className="model-categories">
              {Object.entries(MODEL_INFO).map(([catKey, catInfo]) => (
                <div
                  key={catKey}
                  className={`category-card ${selectedCategory === catKey ? "selected" : ""}`}
                  onClick={() => {
                    setSelectedCategory(catKey);
                    setSelectedModel("");
                  }}
                >
                  <h3>{catInfo.label}</h3>
                  <p>{catInfo.description}</p>
                </div>
              ))}
            </div>

            {selectedCategory && (
              <div className="model-selection">
                <h3>Select Specific Model</h3>
                <div className="models-list">
                  {MODEL_INFO[selectedCategory].models.map((model) => (
                    <div
                      key={model.value}
                      className={`model-option ${selectedModel === model.value ? "selected" : ""}`}
                      onClick={() => setSelectedModel(model.value)}
                    >
                      <div className="option-radio">
                        <input
                          type="radio"
                          name="model"
                          value={model.value}
                          checked={selectedModel === model.value}
                          onChange={() => setSelectedModel(model.value)}
                        />
                      </div>
                      <div className="option-content">
                        <strong>{model.label}</strong>
                        <p>{model.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Configure Analysis */}
        {step === 3 && (
          <div className="wizard-step">
            <h2>Configure Analysis</h2>
            <p className="step-description">
              Choose the column to predict (target) and the columns to use as
              inputs (features).
            </p>

            {["REGRESSION", "CLASSIFICATION", "TIME_SERIES"].includes(
              selectedCategory,
            ) && (
              <div className="config-group" style={{ marginBottom: "20px" }}>
                <label>Target Variable (required) *</label>
                <p
                  style={{
                    fontSize: "13px",
                    color: "var(--text-grey)",
                    margin: "4px 0 8px 0",
                  }}
                >
                  The column you want the model to predict.
                </p>
                <select
                  value={targetVariable}
                  onChange={(e) => setTargetVariable(e.target.value)}
                  className="config-select"
                  required
                  style={{ maxWidth: "400px" }}
                >
                  <option value="">Select target variable...</option>
                  {datasetColumns.map((col) => (
                    <option key={col.key} value={col.key}>
                      {col.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="config-group">
              <label>Features (inputs) *</label>
              <p
                style={{
                  fontSize: "13px",
                  color: "var(--text-grey)",
                  margin: "4px 0 8px 0",
                }}
              >
                Select at least one column to use as input for the model. Do not
                select the target.
              </p>
              <div className="features-list">
                {datasetColumns.map((col) => (
                  <label key={col.key} className="feature-checkbox">
                    <input
                      type="checkbox"
                      checked={selectedFeatures.includes(col.key)}
                      onChange={() => handleFeatureToggle(col.key)}
                      disabled={col.key === targetVariable}
                    />
                    <span>
                      {col.name}
                      {col.key === targetVariable ? " (target)" : ""}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {selectedCategory === "TIME_SERIES" && (
              <div className="config-group">
                <label>Forecast Periods</label>
                <input
                  type="number"
                  min="1"
                  value={otherParams.periods || 12}
                  onChange={(e) =>
                    setOtherParams({
                      ...otherParams,
                      periods: parseInt(e.target.value),
                    })
                  }
                  className="config-input"
                />
              </div>
            )}

            {(selectedModel === "gradient_boosting_regression" ||
              selectedModel === "gradient_boosting_classification") && (
              <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                <div className="config-group">
                  <label>Estimators (n_estimators)</label>
                  <input
                    type="number"
                    min="10"
                    max="500"
                    value={otherParams.n_estimators || 100}
                    onChange={(e) =>
                      setOtherParams({
                        ...otherParams,
                        n_estimators: parseInt(e.target.value),
                      })
                    }
                    className="config-input"
                  />
                </div>
                <div className="config-group">
                  <label>Learning Rate</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.001"
                    max="1"
                    value={otherParams.learning_rate || 0.1}
                    onChange={(e) =>
                      setOtherParams({
                        ...otherParams,
                        learning_rate: parseFloat(e.target.value),
                      })
                    }
                    className="config-input"
                  />
                </div>
                <div className="config-group">
                  <label>Max Depth</label>
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={otherParams.max_depth || 3}
                    onChange={(e) =>
                      setOtherParams({
                        ...otherParams,
                        max_depth: parseInt(e.target.value),
                      })
                    }
                    className="config-input"
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step 4: Review & Run */}
        {step === 4 && (
          <div className="wizard-step">
            <h2>Review & Run</h2>
            <p className="step-description">
              Verify your settings before executing
            </p>

            <div className="review-summary">
              <div className="review-item">
                <label>Dataset:</label>
                <span>{selectedDataset?.name}</span>
              </div>
              <div className="review-item">
                <label>Model Category:</label>
                <span>{MODEL_INFO[selectedCategory]?.label}</span>
              </div>
              <div className="review-item">
                <label>Model:</label>
                <span>
                  {
                    MODEL_INFO[selectedCategory]?.models.find(
                      (m) => m.value === selectedModel,
                    )?.label
                  }
                </span>
              </div>
              <div className="review-item">
                <label>Features Selected:</label>
                <span>{selectedFeatures.length}</span>
              </div>
              {targetVariable && (
                <div className="review-item">
                  <label>Target Variable:</label>
                  <span>
                    {datasetColumns.find((c) => c.key === targetVariable)?.name}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="wizard-footer">
        <button
          className="btn btn-secondary"
          onClick={() =>
            step > 1 ? setStep(step - 1) : navigate("/analyst/analysis")
          }
        >
          {step === 1 ? "Cancel" : "Back"}
        </button>

        {step < 4 ? (
          <button
            className="btn btn-primary"
            onClick={() => setStep(step + 1)}
            disabled={
              (step === 1 && !selectedDataset) ||
              (step === 2 && !selectedModel) ||
              (step === 3 && selectedFeatures.length === 0)
            }
          >
            Next
          </button>
        ) : (
          <button
            className="btn btn-primary btn-primary-lg"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "Running..." : "Run Analysis"}
          </button>
        )}
      </div>
    </div>
  );
};

export default AnalysisWizard;
